import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { UsersPicker, tailwindOverride } from '@getsynapse/design-system';
import { UserAvatars, UserOption } from 'utils/customTypes';
import { PROJECT_PARTICIPANT_TYPE } from 'utils/constants';
import { getCurrentUserParticipantType } from 'state/Project/projectSlice';
import useHasUserAccess from 'Pages/ProjectPage/hooks/useHasUserAccess';
import AssignToMeButton from './AssignToMeButton';

const OwnersPicker: React.FC<{
  availableUsers: UserAvatars[];
  selectedUsers: UserAvatars[];
  currentUser: UserAvatars;
  onChangeOwners: (owners: UserOption[]) => void;
  isReadOnly?: boolean;
  sidePanel?: boolean;
  unlimitedProjectOwners?: boolean;
}> = ({
  availableUsers,
  selectedUsers,
  currentUser,
  onChangeOwners,
  isReadOnly = false,
  sidePanel = false,
  unlimitedProjectOwners = false,
}) => {
  const { permissionsLevel } = useHasUserAccess();
  const participantType = useSelector(getCurrentUserParticipantType);
  const isCurrentUserNotProjectParticipant =
    participantType === PROJECT_PARTICIPANT_TYPE.NOT_PARTICIPANT;
  const isCurrentUserAdminOrManager =
    permissionsLevel === PROJECT_PARTICIPANT_TYPE.OWNER;

  const usersList = useMemo(() => {
    if (isCurrentUserAdminOrManager) {
      const currentUserIndex = availableUsers.findIndex(
        (user: UserAvatars) => user.value === currentUser.value
      );
      if (currentUserIndex > -1) {
        return [
          {
            ...currentUser,
            label: `${currentUser.label} (${intl.get(
              'PROJECT_DETAIL.ASSIGN_TO_ME'
            )})`,
          },
          ...availableUsers.slice(0, currentUserIndex),
          ...availableUsers.slice(currentUserIndex + 1),
        ];
      }
    }
    return availableUsers;
  }, [isCurrentUserAdminOrManager, availableUsers, currentUser]);

  const isCurrentAmongSelectedUsers = selectedUsers.find(
    (user: UserAvatars) => user.value === currentUser.value
  );

  const displayAssignToMeButton =
    isCurrentUserNotProjectParticipant && !isCurrentAmongSelectedUsers;

  return (
    <div
      className={tailwindOverride('flex w-full', {
        'flex-col items-start gap-2': sidePanel,
        'flex-wrap gap-2': !sidePanel,
      })}
    >
      <div
        className={tailwindOverride({
          'min-w-60 flex-1': !sidePanel,
          'w-full': sidePanel,
        })}
      >
        <UsersPicker
          deactivatedUserText={intl.get('DEACTIVATED')}
          disabled={isReadOnly}
          usersList={usersList}
          selectedUsersList={selectedUsers}
          onChange={onChangeOwners}
          maxLimit={unlimitedProjectOwners ? undefined : 6}
          required
          triggerProps={{ 'data-testid': 'owners-picker-trigger' }}
          triggerClassName={tailwindOverride('py-1.5', {
            'py-px': selectedUsers.length > 0,
          })}
        />
      </div>
      {displayAssignToMeButton && (
        <AssignToMeButton
          currentUser={currentUser}
          assignToMeCallback={() => onChangeOwners([currentUser])}
          disabled={selectedUsers.length === 2}
          isUserAdminOrManager={isCurrentUserAdminOrManager}
        />
      )}
    </div>
  );
};

export default OwnersPicker;
